import { ButtonLink, Heading5 } from '@blueprint/ui';
import { SaveOutlineSmall, ArrowRightSmall } from '@blueprint/icons';
import { ListingImage } from '@zoopla/listings/image';
import { ListingFeatureList } from '@zoopla/listings/features';
import { LifecycleTag } from '@zoopla/shared/components';
import {
    WidgetWrapper,
    HeadingRow,
    StyledListingCardScrollRow
} from './SavedListingsWidgetDesktop.styles';
import {
    StyledListingCardPortraitDetails,
    StyledListingCardPortraitAddress,
    StyledListingCardPortraitPrice,
    StyledListingCardPortraitTitle
} from '@zoopla/listings/cards';
import {
    trackClickSavedListingWidgetCard,
    trackClickSeeAllSavedListings
} from '../SavedListingsWidget.tracking';
import {
    TriggerWhenVisible,
    ListingCardPortrait,
    ListingCardPortraitSkeleton
} from '@zoopla/listings/cards';
import { MappedEnrichedSavedPropertyId } from '../graphql/EnrichedSavedPropertyIds/useEnrichedSavedPropertyIds';
import { useListing } from '../graphql/Listing/useListing';

export const SavedListingCardDesktop = ({
    listingId,
    isTriggered
}: {
    listingId: string;
    isTriggered: boolean;
}) => {
    const listing = useListing(listingId, !isTriggered);

    if (!listing) {
        return <ListingCardPortraitSkeleton />;
    }

    const {
        imagePreview,
        featurePreview,
        displayAddress,
        listingLinkHref,
        displayPrice,
        title,
        publicationStatus,
        section,
        statusSummary,
        tags
    } = listing;

    return (
        <ListingCardPortrait
            testId="saved-property-card-desktop"
            listingLinkHref={listingLinkHref}
            onClick={trackClickSavedListingWidgetCard}
        >
            <ListingImage image={imagePreview} useImageLazyLoad={true} />
            <StyledListingCardPortraitDetails
                data-testid={`saved-property-card-details-${listingId}`}
            >
                <LifecycleTag
                    publicationStatus={publicationStatus}
                    section={section}
                    statusSummary={statusSummary}
                    tags={tags}
                />
                <StyledListingCardPortraitTitle>
                    {title}
                </StyledListingCardPortraitTitle>
                <StyledListingCardPortraitPrice>
                    {displayPrice}
                </StyledListingCardPortraitPrice>
                <StyledListingCardPortraitAddress>
                    {displayAddress}
                </StyledListingCardPortraitAddress>
                <ListingFeatureList features={featurePreview} />
            </StyledListingCardPortraitDetails>
        </ListingCardPortrait>
    );
};

export const SavedListingsWidgetDesktop = ({
    listings
}: {
    listings: Array<MappedEnrichedSavedPropertyId>;
}) => {
    return (
        <WidgetWrapper
            data-testid="saved-property-widget"
            aria-labelledby="saved-properties-widget-header"
        >
            <HeadingRow>
                <SaveOutlineSmall />
                <Heading5 size="6" id="saved-properties-widget-header">
                    Recently saved properties
                </Heading5>
                <ButtonLink
                    variant="tertiary"
                    href="/favourites/properties/"
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedListings}
                >
                    See all saved properties
                    <ArrowRightSmall />
                </ButtonLink>
            </HeadingRow>
            <StyledListingCardScrollRow>
                {listings.map(({ listingId }) => {
                    return (
                        <TriggerWhenVisible triggerDelay={100} key={listingId}>
                            {(isTriggered: boolean) => {
                                return (
                                    <SavedListingCardDesktop
                                        listingId={listingId}
                                        isTriggered={isTriggered}
                                    />
                                );
                            }}
                        </TriggerWhenVisible>
                    );
                })}
            </StyledListingCardScrollRow>
        </WidgetWrapper>
    );
};
